// OPTIONS · Scanner · SELL OPTIONS table (SELL OPTIONS plan, Phase 3). Read-only.
// Sells one CE lot + one PE lot of the same underlying and expiry - ANY strikes (ITM / ATM / OTM or mixed).
// Every number comes from the worker (backend option_sell_math, the arithmetic the trading engine uses):
// each leg is priced at the bid depth one lot takes, never the LTP; costs are on the right sides (NFO / MCX).
// Ranking = highest net credit. Protection % (one value, both sides) only decides which pairs are listed.
import React, { useEffect, useMemo, useState } from "react";
import RowStateBadge from "@/components/RowStateBadge";
import { useEntryWindows, withEntryWindow } from "@/lib/entryWindow";
import { toast } from "sonner";
import optionsApi, { optionsError } from "@/lib/optionsApi";
import { rupees } from "@/components/options/PairValuation";
import { age, px, qty, usePoll } from "@/components/options/OptionsCommon";
import ResetDefaultsButton from "@/components/ResetDefaultsButton";
import { ColumnsMenu, CONTROL, FilterBar, FilterField, ScannerSettingsButton, ScannerTable, useColumnPrefs } from "@/components/options/ScannerTable";
import { visibleColumns } from "@/lib/columnPrefs";
import { marginCell, windowText } from "@/lib/optionsText";

const dmy = (iso) => (/^\d{4}-\d{2}-\d{2}$/.test(iso || "") ? iso.split("-").reverse().join("-") : iso || "—");
const pctTxt = (v) => (v === null || v === undefined ? "—" : v === Infinity || v > 1e8 ? "∞" : `${Number(v).toFixed(2)}%`);
const moneyness = (side, strike, spot) => {
  if (!spot) return "";
  if (strike === spot) return "ATM";
  return (side === "CE" ? strike < spot : strike > spot) ? "ITM" : "OTM";
};

/** Protection % (Both Sides): ONE value; blank = no protection filter. Saved to the scanner settings. */
export function ProtectionControl({ value, onSaved }) {
  const [v, setV] = useState("");
  const shown = v !== "" ? v : (value ?? "");
  const save = async () => {
    if (v === "") return;
    const n = v.trim() === "" || v.trim() === "0" ? null : Number(v);
    if (n !== null && (!Number.isFinite(n) || n <= 0 || n >= 100)) { toast.error("Protection % must be above 0 and below 100 (0 clears it)"); return; }
    if (n === value) { setV(""); return; }
    try { await optionsApi.put("/settings", { protection_pct: n }); toast.success(n ? `Protection ${n}% both sides` : "Protection filter cleared"); setV(""); onSaved(); }
    catch (e) { toast.error(optionsError(e)); }
  };
  return (
    <FilterField label="Protection % (both sides)" title="One percentage for BOTH sides of the underlying: a pair is listed only if its theoretical expiry P&L, after estimated costs, is ≥ 0 everywhere from spot −% to spot +%. 0 clears it. (saved scanner setting)">
      <input value={shown} onChange={(e) => setV(e.target.value)} onBlur={save} onKeyDown={(e) => e.key === "Enter" && save()}
        placeholder="none" className={`${CONTROL} w-24 mono text-right`} data-testid="options-protection-pct" />
    </FilterField>
  );
}

/** Minimum Expiry Profit (₹): with Protection % = the LOWEST theoretical net expiry P&L anywhere in the band;
 *  without it = the BEST-case net expiry P&L (one underlying price - never a minimum). Blank = no filter. */
export function MinExpiryProfitControl({ value, banded, onSaved }) {
  const [v, setV] = useState("");
  const shown = v !== "" ? v : (value ?? "");
  const save = async () => {
    if (v === "") return;
    const n = v.trim() === "-" ? null : (v.trim() === "" ? null : Number(v));
    if (n !== null && !Number.isFinite(n)) { toast.error("Minimum Expiry Profit must be an amount in ₹ (blank clears it)"); return; }
    if (n === value) { setV(""); return; }
    try { await optionsApi.put("/settings", { min_expiry_profit: n }); toast.success(n === null ? "Minimum Expiry Profit cleared" : `Minimum Expiry Profit ₹${n}`); setV(""); onSaved(); }
    catch (e) { toast.error(optionsError(e)); }
  };
  const clear = async () => {
    try { await optionsApi.put("/settings", { min_expiry_profit: null }); toast.success("Minimum Expiry Profit cleared"); setV(""); onSaved(); }
    catch (e) { toast.error(optionsError(e)); }
  };
  return (
    <FilterField label="Min Expiry Profit ₹" title={banded
      ? "Only pairs whose LOWEST theoretical net P&L at expiry anywhere in the Protection % range (after estimated costs) is at least this"
      : "No Protection %: compared with the BEST-CASE net P&L at expiry (one underlying price) - not a profit over any range"}>
      <span className="flex items-center gap-1">
        <input value={shown} onChange={(e) => setV(e.target.value)} onBlur={save} onKeyDown={(e) => e.key === "Enter" && save()}
          placeholder="none" className={`${CONTROL} w-24 mono text-right`} data-testid="options-min-expiry-profit" />
        {value != null && <button type="button" className="text-[10px] underline text-[var(--term-muted)]" onClick={clear}
          data-testid="options-min-expiry-profit-clear">clear</button>}
      </span>
    </FilterField>
  );
}

// Columns (2026-10-14): Trade, Underlying, Und. Price are PROTECTED (always shown, in place); every other column
// is optional (Columns menu - SELL preferences are separate from BUY).
const TD = "px-1.5 py-1 text-right whitespace-nowrap mono";
const S_ = (r) => r.SELL || {};
const scol = (id, label, cell, extra = {}) => ({ id, label, cell, td: TD, ...extra });

function MarginCell({ capital }) {
  const m = marginCell(capital);
  if (m.value != null) return <span title={`Zerodha SELL basket margin for one lot of each leg${m.stale ? " (older than a minute)" : ""}`} className={m.stale ? "opacity-60" : ""}>{rupees(m.value)}</span>;
  return <span className="text-[10px] text-[var(--term-muted)]" title={m.title} data-testid="options-sell-margin-reason">{m.text}</span>;
}

export const SELL_PROTECTED = [
  { id: "trade", label: "Trade", align: "center", td: "px-1.5 py-1 text-center", cell: (r, c) => (
    <button type="button" data-testid={`options-sell-trade-${r.pair_id}`}
      title="Open the SELL ticket for this pair (nothing is created or sent until you confirm)"
      className="px-3 py-0.5 rounded-sm text-[11px] font-bold bg-[#B45309] text-white hover:bg-[#92400e]"
      onClick={(e) => { e.stopPropagation(); c.onOpen(r.pair_id, "hedge"); }}>SELL</button>) },
  scol("underlying", "Underlying", (r) => r.underlying, { align: "left", td: "px-1.5 py-1 text-left font-bold whitespace-nowrap" }),
  scol("underlying_ltp", "Und. Price", (r) => px(r.underlying_ltp)),
];

export const SELL_OPTIONAL = [
  scol("state", "State", (r) => <RowStateBadge state={r.state} testId={`sell-state-${r.pair_id}`} />, { align: "left" }),
  scol("expiry", "Expiry", (r) => dmy(r.expiry)),
  scol("ce_strike", "CE Strike", (r) => <span title={r.ce.tradingsymbol}>{r.ce.strike} <span className="text-[9px] text-[var(--term-muted)]">{moneyness("CE", r.ce.strike, r.underlying_ltp)}</span></span>, { td: `${TD} font-semibold` }),
  scol("ce_sell", "CE Sell", (r) => <span title={`bid ${px(r.ce.bid)} · LIMIT ${px(r.ce.sell_limit)} · ${qty(r.ce.bid_depth_qty)} on the bids`}>{px(r.ce.sell_vwap)}</span>),
  scol("ce_oi", "CE OI", (r) => qty(r.ce.oi)),
  scol("pe_strike", "PE Strike", (r) => <span title={r.pe.tradingsymbol}>{r.pe.strike} <span className="text-[9px] text-[var(--term-muted)]">{moneyness("PE", r.pe.strike, r.underlying_ltp)}</span></span>, { td: `${TD} font-semibold` }),
  scol("pe_sell", "PE Sell", (r) => <span title={`bid ${px(r.pe.bid)} · LIMIT ${px(r.pe.sell_limit)} · ${qty(r.pe.bid_depth_qty)} on the bids`}>{px(r.pe.sell_vwap)}</span>),
  scol("pe_oi", "PE OI", (r) => qty(r.pe.oi)),
  scol("credit", "Credit ₹", (r) => <span data-testid={`options-sell-credit-${r.pair_id}`}>{rupees(S_(r).credit)}</span>),
  scol("costs", "Costs ₹", (r) => <span title="Entry SELL + estimated BUY-to-close charges (close at the dearer of premium and band intrinsic)">{rupees(S_(r).costs)}</span>),
  scol("net_credit", "Net Credit ₹", (r) => <span title="Credit − costs (close at the premium): the ranking">{rupees(S_(r).rank_net_credit)}</span>, { td: `${TD} font-bold bg-[#B45309]/10` }),
  scol("required", "Required ₹", (r) => <span title="Minimum combined credit for the Protection % (blank % = none)">{S_(r).required == null ? "—" : rupees(S_(r).required)}</span>),
  scol("band", "Band (L – U)", (r) => (S_(r).lower == null ? "—" : `${px(S_(r).lower)} – ${px(S_(r).upper)}`)),
  scol("breakevens", "Break-evens", (r) => <span title="Expiry break-evens after costs">{px(S_(r).lower_be)} / {px(S_(r).upper_be)}</span>),
  scol("protection", "Protection", (r) => <span data-testid={`options-sell-protection-${r.pair_id}`}>{pctTxt(S_(r).achieved_pct)}</span>),
  scol("expiry_pnl", "Expiry P&L ₹", (r) => {
    const S = S_(r);
    return (<span data-testid={`options-sell-expiry-pnl-${r.pair_id}`}
      title={S.min_expiry_pnl != null ? "LOWEST theoretical net P&L at expiry anywhere in the protected range, after estimated costs"
        : "BEST-CASE theoretical net P&L at expiry (one underlying price), after estimated costs - not a minimum: losses are possible elsewhere"}>
      {rupees(S.min_expiry_pnl != null ? S.min_expiry_pnl : S.max_expiry_pnl)}</span>);
  }, { head: (c) => (c.banded ? "Min expiry P&L ₹ (in band)" : "Max expiry P&L ₹ (best case)"),
    td: (r) => `${TD} ${S_(r).min_expiry_pnl != null ? (S_(r).min_expiry_pnl >= 0 ? "text-[#00C805]" : "text-[#FF3B30]") : ""}` }),
  scol("margin", "Margin", (r) => <MarginCell capital={r.capital} />),
  scol("lot", "Lot", (r) => r.lot_size ?? "—"),
];
const sellRowProps = (onOpen) => (r) => ({ className: "gz-row cursor-pointer hover:bg-[var(--term-hover)]", onClick: () => onOpen(r.pair_id),
  "data-testid": `options-sell-row-${r.pair_id}`, title: `SELL ${r.ce.tradingsymbol} + SELL ${r.pe.tradingsymbol}` });

// Display order only - the scanner's and the engine's ranking stay "highest net credit" (the default here)
export const SELL_SORTS = {
  net: { label: "Net credit (engine ranking)", key: (r) => r.SELL?.rank_net_credit },
  expiry: { label: "Expiry profit", key: (r) => r.SELL?.min_expiry_pnl ?? r.SELL?.max_expiry_pnl },
  per_margin: { label: "Expiry profit ÷ margin", key: (r) => {
    const p = r.SELL?.min_expiry_pnl ?? r.SELL?.max_expiry_pnl, m = r.capital?.capital_required;
    return p != null && m > 0 ? p / m : null; } },
};
export function sortSellRows(rows, by) {
  const k = (SELL_SORTS[by] || SELL_SORTS.net).key;
  return [...rows].sort((a, b) => {
    const x = k(a), y = k(b);
    if (x == null && y == null) return 0;
    if (x == null) return 1;
    if (y == null) return -1;
    return y - x;
  });
}

export default function SellScanner({ settings, onSaved, onOpen, paused, onData }) {
  const opp = usePoll("/opportunities", { ms: 2000, paused, params: { direction: "SELL", limit: 500 } });
  const [sortBy, setSortBy] = useState("net");
  const [underlying, setUnderlying] = useState("");
  const [expiry, setExpiry] = useState("");
  const [q, setQ] = useState("");
  const d = opp.data;
  const building = !d || d.direction !== "SELL";
  const raw = useMemo(() => (d?.direction === "SELL" ? d.rows || [] : []), [d]);
  const windows = useEntryWindows(raw);                                   // W5: the trader's own entry rule
  const all = useMemo(() => raw.map((r) => withEntryWindow(r, windows)), [raw, windows]);
  const unds = [...new Set(all.map((r) => r.underlying))].sort();
  const exps = [...new Set(all.filter((r) => !underlying || r.underlying === underlying).map((r) => r.expiry))].sort();
  const rows = sortSellRows(all.filter((r) => (!underlying || r.underlying === underlying) && (!expiry || r.expiry === expiry)
    && (!q.trim() || `${r.underlying} ${r.ce.tradingsymbol} ${r.pe.tradingsymbol}`.toUpperCase().includes(q.trim().toUpperCase()))), sortBy);
  const banded = settings?.protection_pct != null;
  const win = settings?.sell_ce_itm_strikes != null ? { ce_itm: settings.sell_ce_itm_strikes, ce_otm: settings.sell_ce_otm_strikes,
    pe_itm: settings.sell_pe_itm_strikes, pe_otm: settings.sell_pe_otm_strikes } : null;
  const sellCols = useColumnPrefs("SELL", SELL_OPTIONAL);
  const sellVisible = useMemo(() => visibleColumns(SELL_PROTECTED, SELL_OPTIONAL, sellCols.prefs), [sellCols.prefs]);
  const ctx = useMemo(() => ({ banded, onOpen }), [banded, onOpen]);
  const rowPropsFn = useMemo(() => sellRowProps(onOpen), [onOpen]);
  useEffect(() => { if (onData) onData(d?.direction === "SELL" ? d : null); }, [d, onData]);
  // view filters cleared; Protection % and Minimum Expiry Profit are saved scanner settings, set to the recommended values
  const resetDefaults = async () => {
    setUnderlying(""); setExpiry(""); setQ(""); setSortBy("net");
    try {
      const r = await optionsApi.get("/settings/defaults");
      const patch = {};
      for (const k of ["protection_pct", "min_expiry_profit"]) if (r.data && k in r.data && r.data[k] !== settings?.[k]) patch[k] = r.data[k];
      if (Object.keys(patch).length) await optionsApi.put("/settings", patch);
      toast.success("Recommended SELL filters restored");
      onSaved(); opp.reload();
    } catch (e) { toast.error(optionsError(e)); }
  };
  return (
    <div className="space-y-2" data-testid="options-sell-scanner">
      <div className="text-[11px] px-2 py-1.5 rounded-sm bg-[#B45309]/10 text-[#B45309]" data-testid="options-sell-banner">
        SELL OPTIONS: short CE + short PE. Protection % is an <b>expiry break-even condition only</b> — not a stop-loss. The position can lose
        before expiry, and losses outside the protected range are not limited. Expiry P&L figures are theoretical, at expiry, after estimated
        costs: with Protection % the column is the <b>lowest</b> P&L inside the range; without it, the <b>best case</b> at a single price.
      </div>
      <FilterBar testId="options-sell-filters" actions={<>
        <ColumnsMenu protectedCols={SELL_PROTECTED} optional={SELL_OPTIONAL} {...sellCols} testId="options-sell-columns" />
        <ResetDefaultsButton onReset={resetDefaults} testId="options-sell-reset-defaults"
          confirm="Restore the recommended SELL filters? Underlying, expiry, search and sort are cleared, and Protection % and Minimum Expiry Profit are saved at the recommended values."
          title="Clear the view filters and set Protection % and Minimum Expiry Profit to the recommended values" />
        <ScannerSettingsButton />
      </>}>
        <FilterField label="Underlying">
          <select value={underlying} onChange={(e) => setUnderlying(e.target.value)} data-testid="options-sell-underlying-filter" className={CONTROL}>
            <option value="">All underlyings</option>
            {unds.map((n) => <option key={n} value={n}>{n}</option>)}
          </select></FilterField>
        <FilterField label="Expiry">
          <select value={expiry} onChange={(e) => setExpiry(e.target.value)} data-testid="options-sell-expiry-filter" className={CONTROL} disabled={exps.length < 2}>
            <option value="">{exps.length === 1 ? dmy(exps[0]) : "All expiries"}</option>
            {exps.length > 1 && exps.map((x) => <option key={x} value={x}>{dmy(x)}</option>)}
          </select></FilterField>
        <FilterField label="Sort" title="Display order only. The engine always ranks by highest executable net credit among pairs meeting Protection % and the trade's Minimum Expiry Profit.">
          <select className={CONTROL} value={sortBy} onChange={(e) => setSortBy(e.target.value)} data-testid="options-sell-sort">
            {Object.entries(SELL_SORTS).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
          </select></FilterField>
        <ProtectionControl value={settings?.protection_pct} onSaved={() => { onSaved(); opp.reload(); }} />
        <MinExpiryProfitControl value={settings?.min_expiry_profit} banded={banded} onSaved={() => { onSaved(); opp.reload(); }} />
        <FilterField label="Strike window" title="Set in Scanner Settings: strikes ITM / OTM of the ATM, per option type">
          <span className={`${CONTROL} flex items-center mono`} data-testid="options-sell-window">{win ? windowText(win) : "not set"}</span>
        </FilterField>
        <FilterField label="Search">
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Symbol" className={`${CONTROL} w-32`} data-testid="options-sell-search" />
        </FilterField>
      </FilterBar>
      <div className="text-[var(--term-muted)] mono text-xs" data-testid="options-sell-counts">
        {d?.running === false ? "scanner not running" : building ? "building the SELL table…"
          : `${rows.length} pairs · ${d?.protection_pct ? `protected ±${d.protection_pct}%` : "no protection filter"}${d?.min_expiry_profit != null ? ` · min expiry profit ₹${d.min_expiry_profit} (${d?.protection_pct ? "lowest in band" : "best case"})` : ""}${d?.generated_at ? ` · ${new Date(d.generated_at).toLocaleTimeString()}` : ""}`}
        {opp.error && <span className="ml-2 text-[#FF3B30]">{opp.error}</span>}
      </div>
      <ScannerTable testId="options-sell-table" cols={sellVisible} rows={rows} rowKey={(r) => r.pair_id} rowProps={rowPropsFn} ctx={ctx}
        maxHeight="calc(100vh - 340px)"
        empty={<span data-testid="options-sell-empty">{d?.running === false ? "Start the scanner to see SELL pairs." : building ? "Building the SELL table…" :
          "No CE + PE pair meets the conditions right now (bid depth for one lot on both legs, Minimum OI, fresh book, Protection %). The list updates by itself."}</span>} />
      <div className="text-[10px] text-[var(--term-muted)]">
        Each leg is priced at the bids one lot takes (several levels if needed), never the LTP. Credit = both legs × lot × units.
        Required = the larger expiry liability at spot −% / +% plus estimated costs. Updated {age((Date.now() - new Date(d?.generated_at || Date.now()).getTime()) / 1000)} ago.
      </div>
    </div>
  );
}
